import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Booking, Facility, BookingContextType, TimeSlot } from '../types';
import type { ApiFacility } from '../types/businessProfile';
import type { FacilityBookingsResponse } from '../types/facilityBookings';
import { useAuth } from './AuthContext';
import { businessProfileService } from '../services/businessProfileService';
import { facilityService } from '../services/facilityService';
import { reservationService } from '../services/reservationService';
import { trainerAssociationService } from '../services/trainerAssociationService';
import type { ReservationWithSlotsResponse, CancelSlotsResponse, ReservationResponse, ApplyProductRequest, ApplyProductResponse, RescheduleReservationRequest, RescheduleReservationResponse, GroupReservationRequest, GroupReservationResponse } from '../types/reservation';
import type { AvailableTrainer } from '../types/trainerAssociation';

const BookingContext = createContext<BookingContextType | undefined>(undefined);

// Facilities and bookings always come from the API. Never seed mock data here -
// a failed fetch must surface as an empty/error state, not fake "John Doe" rows.
const initialFacilities: Facility[] = [];
const initialBookings: Booking[] = [];

// Helper function to transform API facility to app facility
const transformApiFacility = (apiFacility: ApiFacility): Facility => {
  return {
    id: apiFacility.id,
    name: apiFacility.name,
    type: apiFacility.type?.toLowerCase() ?? 'other',
    description: apiFacility.description,
    capacity: apiFacility.capacity,
    pricePerHour: apiFacility.pricePerHour,
    grossPricePerHour: apiFacility.grossPricePerHour,
    city: apiFacility.city,
  };
};

export const BookingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>(initialBookings);
  const [facilities, setFacilities] = useState<Facility[]>(initialFacilities);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [businessProfile, setBusinessProfile] = useState<{
    id: string;
    companyName: string;
    displayName: string;
    avatarUrl: string;
  } | null>(null);
  const [facilityTimeSlots, setFacilityTimeSlots] = useState<{
    [facilityId: string]: {
      [date: string]: FacilityBookingsResponse['timeSlots'];
    };
  }>({});

  // Fetch business profile data when user is authenticated
  const fetchBusinessProfile = async () => {
    if (!token) {
      setError('No authentication token available');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const profile = await businessProfileService.getDashboard(token);

      // Store business profile data
      setBusinessProfile({
        id: profile.id,
        companyName: profile.companyName,
        displayName: profile.displayName,
        avatarUrl: profile.avatarUrl,
      });

      // Transform API facilities to app facilities
      const transformedFacilities = profile.facilities.map(transformApiFacility);
      setFacilities(transformedFacilities);

      // Set first facility as selected if available
      if (transformedFacilities.length > 0) {
        setSelectedFacility(transformedFacilities[0]);
      }

      // TODO: Transform and set bookings from API if available in the response
      // For now, keep using mock bookings until we have booking API endpoints
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch business profile';
      setError(errorMessage);
      console.error('Failed to fetch business profile:', err);

      // Clear any stale data so the UI shows a real error/empty state instead
      // of falling back to mock facilities and bookings.
      setFacilities([]);
      setBookings([]);
      setSelectedFacility(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch facility bookings for a specific date
  const fetchFacilityBookings = useCallback(async (facilityId: string, date: string) => {
    if (!token || !isAuthenticated) {
      console.warn('Cannot fetch bookings: not authenticated');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await facilityService.getBookingsByDate(facilityId, date, token);

      // Transform API reservations to our Booking type
      // Use detailedTimeSlots to get individual slot information with status
      const transformedBookings: Booking[] = response.reservations.flatMap(reservation => {
        console.log('Processing reservation:', reservation.id, reservation);

        // Check if we have detailedTimeSlots (new API format)
        if (reservation.detailedTimeSlots && reservation.detailedTimeSlots.length > 0) {
          console.log('Using detailedTimeSlots:', reservation.detailedTimeSlots);

          // Filter only Active slots (skip Cancelled slots)
          const activeSlots = reservation.detailedTimeSlots
            .filter(slot => slot.status === 'Active')
            .sort((a, b) => a.timeSlot.localeCompare(b.timeSlot));

          if (activeSlots.length === 0) {
            console.log('No active slots found');
            return [];
          }
        } else if (reservation.timeSlots && reservation.timeSlots.length > 0) {
          // Fallback to old format using timeSlots array
          console.log('Using timeSlots (old format):', reservation.timeSlots);
          const activeSlots = reservation.timeSlots.map(slot => ({
            id: `${reservation.id}-${slot}`,
            timeSlot: `${slot}-${addOneHour(slot)}`,
            slotPrice: 0,
            status: 'Active' as const,
            cancelledAt: null,
            cancellationReason: null,
          })).sort((a, b) => a.timeSlot.localeCompare(b.timeSlot));

          reservation.detailedTimeSlots = activeSlots;
        } else {
          console.warn('Reservation has no timeSlots or detailedTimeSlots:', reservation);
          return [];
        }

        const activeSlots = reservation.detailedTimeSlots
          .filter(slot => slot.status === 'Active')
          .sort((a, b) => a.timeSlot.localeCompare(b.timeSlot));

        if (activeSlots.length === 0) return [];

        const bookings: Booking[] = [];

        // Group consecutive slots together for display
        let i = 0;
        while (i < activeSlots.length) {
          const currentSlot = activeSlots[i];

          // Parse timeSlot - it might be "10:00" or "10:00-11:00"
          let startTime: string;
          let endTime: string;

          if (currentSlot.timeSlot.includes('-')) {
            // Format: "08:00-09:00"
            const parts = currentSlot.timeSlot.split('-');
            startTime = parts[0];
            endTime = parts[1];
          } else {
            // Format: "10:00" (just start time)
            startTime = currentSlot.timeSlot;
            endTime = addOneHour(startTime);
          }

          // Look ahead for consecutive slots
          let j = i + 1;
          while (j < activeSlots.length) {
            const nextSlot = activeSlots[j];
            let nextStart: string;

            if (nextSlot.timeSlot.includes('-')) {
              nextStart = nextSlot.timeSlot.split('-')[0];
            } else {
              nextStart = nextSlot.timeSlot;
            }

            // Check if next slot starts where current ends
            if (nextStart === endTime) {
              endTime = nextSlot.timeSlot.includes('-')
                ? nextSlot.timeSlot.split('-')[1]
                : addOneHour(nextSlot.timeSlot);
              j++;
            } else {
              break;
            }
          }

          // Create booking for this range
          bookings.push({
            id: `${reservation.id}-${startTime}`,
            facilityId: response.facilityId,
            timeSlot: {
              startTime: startTime,
              endTime: endTime,
              date: date,
            },
            customerName: reservation.userName || reservation.guestName || 'Guest',
            customerEmail: reservation.userEmail || reservation.guestEmail || '',
            customerPhone: reservation.userPhone || reservation.guestPhone || '',
            isPaid: reservation.paymentDetails?.isPaid || reservation.paymentDetails?.status === 'COMPLETED' || !!reservation.productPurchaseId || false,
            paymentId: reservation.paymentDetails?.id || undefined,
            createdAt: reservation.createdAt || new Date().toISOString(),
            notes: reservation.trainerDisplayName ? `Trainer: ${reservation.trainerDisplayName}` : '',
            status: activeSlots.length < reservation.detailedTimeSlots!.length ? 'Partial' : 'Active',
            groupId: reservation.groupId ?? null,
          });

          i = j;
        }

        return bookings;
      });

      // Update bookings - replace bookings for this facility and date
      setBookings(prevBookings => {
        const filtered = prevBookings.filter(
          b => !(b.facilityId === facilityId && b.timeSlot.date === date)
        );
        return [...filtered, ...transformedBookings];
      });

      // Store timeslots data
      setFacilityTimeSlots(prev => ({
        ...prev,
        [facilityId]: {
          ...prev[facilityId],
          [date]: response.timeSlots,
        },
      }));

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch facility bookings';
      console.error('Failed to fetch facility bookings:', err);
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated]);

  // Fetch data when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchBusinessProfile();
    } else {
      // Not authenticated - no data to show.
      setFacilities([]);
      setBookings([]);
      setSelectedFacility(null);
    }
  }, [isAuthenticated, token]);

  const createBooking = useCallback(async (booking: Omit<Booking, 'id' | 'createdAt'> & { userId?: string; customPrice?: number; trainerProfileId?: string | null; numberOfUsers?: number; payForAllUsers?: boolean }) => {
    if (!token || !isAuthenticated) {
      throw new Error('User must be authenticated to create a booking');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Extract hours from start and end times to build timeSlots array
      const startHour = parseInt(booking.timeSlot.startTime.split(':')[0]);
      const endHour = parseInt(booking.timeSlot.endTime.split(':')[0]);
      const timeSlots: string[] = [];

      for (let hour = startHour; hour < endHour; hour++) {
        timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
      }

      // Format date as ISO datetime at midnight UTC
      // Convert YYYY-MM-DD to YYYY-MM-DDT00:00:00Z
      const isoDate = `${booking.timeSlot.date}T00:00:00Z`;

      // Call API to create reservation
      const reservation = await facilityService.createReservation(
        {
          facilityId: booking.facilityId,
          userId: null, // Guest bookings don't have a userId
          date: isoDate,
          timeSlots,
          trainerProfileId: booking.trainerProfileId || null,
          customPrice: booking.customPrice || undefined,
          notes: booking.notes || undefined,
          numberOfUsers: booking.numberOfUsers || undefined,
          payForAllUsers: booking.payForAllUsers || undefined,
          guestName: booking.customerName,
          guestPhone: booking.customerPhone || undefined,
          guestEmail: booking.customerEmail || undefined,
        },
        token
      );

      // Refresh bookings for this facility and date
      await fetchFacilityBookings(booking.facilityId, booking.timeSlot.date);

      return reservation;
    } catch (err) {
      // Don't set global error for booking operations to avoid affecting facility list
      console.error('Failed to create booking:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated, fetchFacilityBookings]);

  const updateBooking = (id: string, updatedData: Partial<Booking>) => {
    setBookings(bookings.map(booking =>
      booking.id === id ? { ...booking, ...updatedData } : booking
    ));
  };

  const deleteBooking = useCallback(async (id: string, cancellationNotes?: string) => {
    if (!token || !isAuthenticated) {
      throw new Error('User must be authenticated to cancel a booking');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Extract reservation ID from booking ID format: "reservationId-startTime"
      // Split by the last hyphen to get the reservation ID
      const lastHyphenIndex = id.lastIndexOf('-');
      const reservationId = lastHyphenIndex !== -1 ? id.substring(0, lastHyphenIndex) : id;

      // Call API to cancel the reservation
      await reservationService.cancelReservationByAgent(reservationId, {
        notes: cancellationNotes,
      });

      // Find the booking to get its facility and date for refresh
      const booking = bookings.find(b => b.id === id);

      // Remove booking from local state immediately
      setBookings(prevBookings => prevBookings.filter(b => b.id !== id));

      // Refresh bookings for this facility and date to get updated data
      if (booking) {
        await fetchFacilityBookings(booking.facilityId, booking.timeSlot.date);
      }
    } catch (err) {
      // Don't set global error for booking operations to avoid affecting facility list
      console.error('Failed to cancel booking:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated, bookings, fetchFacilityBookings]);

  const togglePaymentStatus = async (id: string) => {
    if (!token || !isAuthenticated) {
      console.error('User must be authenticated to toggle payment status');
      return;
    }

    // Find the booking
    const booking = bookings.find(b => b.id === id);
    if (!booking || booking.paymentId) {
      console.error('Booking not found or has paymentId');
      return;
    }

    // Only mark as paid if currently unpaid
    if (!booking.isPaid) {
      try {
        // Extract reservation ID from booking ID format: "reservationId-startTime"
        const lastHyphenIndex = id.lastIndexOf('-');
        const reservationId = lastHyphenIndex !== -1 ? id.substring(0, lastHyphenIndex) : id;

        // Toggle reservation payment status via API
        await reservationService.toggleReservationPayment(reservationId);

        // Refresh facility bookings to get updated data
        await fetchFacilityBookings(booking.facilityId, booking.timeSlot.date);
      } catch (err) {
        console.error('Failed to mark reservation as paid:', err);
        // Optionally show error to user
      }
    } else {
      // For unpaid, just do local state update since there's no "mark as unpaid" API
      setBookings(bookings.map(booking => {
        if (booking.id === id && !booking.paymentId) {
          return { ...booking, isPaid: false };
        }
        return booking;
      }));
    }
  };

  const rescheduleBooking = (id: string, newTimeSlot: TimeSlot) => {
    setBookings(bookings.map(booking =>
      booking.id === id ? { ...booking, timeSlot: newTimeSlot } : booking
    ));
  };

  const isTimeSlotBooked = (facilityId: string, timeSlot: TimeSlot): boolean => {
    return bookings.some(booking => {
      if (booking.facilityId !== facilityId || booking.timeSlot.date !== timeSlot.date) {
        return false;
      }

      const bookingStart = parseTime(booking.timeSlot.startTime);
      const bookingEnd = parseTime(booking.timeSlot.endTime);
      const slotStart = parseTime(timeSlot.startTime);
      const slotEnd = parseTime(timeSlot.endTime);

      return (slotStart < bookingEnd && slotEnd > bookingStart);
    });
  };

  const getReservationSlots = useCallback(async (reservationId: string): Promise<ReservationWithSlotsResponse> => {
    if (!token || !isAuthenticated) {
      throw new Error('User must be authenticated to view reservation details');
    }

    try {
      return await reservationService.getReservationSlots(reservationId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch reservation slots';
      console.error('Failed to fetch reservation slots:', err);
      throw new Error(errorMessage);
    }
  }, [token, isAuthenticated]);

  const cancelReservationSlots = useCallback(async (reservationId: string, slotIds: string[]): Promise<CancelSlotsResponse> => {
    if (!token || !isAuthenticated) {
      throw new Error('User must be authenticated to cancel slots');
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await reservationService.cancelSlots(reservationId, { slotIds });

      // Find any bookings for this reservation and refresh them
      const affectedBooking = bookings.find(b => b.id.startsWith(reservationId));
      if (affectedBooking) {
        await fetchFacilityBookings(affectedBooking.facilityId, affectedBooking.timeSlot.date);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel slots';
      // Don't set global error for booking operations to avoid affecting facility list
      console.error('Failed to cancel slots:', err);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated, bookings, fetchFacilityBookings]);

  const toggleReservationPayment = useCallback(async (reservationId: string): Promise<ReservationResponse> => {
    if (!token || !isAuthenticated) {
      throw new Error('User must be authenticated to toggle reservation payment');
    }

    setIsLoading(true);

    try {
      const result = await reservationService.toggleReservationPayment(reservationId);

      // Find the affected booking to refresh its facility bookings
      const affectedBooking = bookings.find(b => b.id.startsWith(reservationId));

      // Refresh facility bookings if we found an affected booking
      if (affectedBooking) {
        await fetchFacilityBookings(affectedBooking.facilityId, affectedBooking.timeSlot.date);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to mark reservation as paid';
      // Don't set global error for booking operations to avoid affecting facility list
      console.error('Failed to mark reservation as paid:', err);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated, bookings, fetchFacilityBookings]);

  const updateReservationNotes = useCallback(async (reservationId: string, notes: string): Promise<ReservationResponse> => {
    if (!token || !isAuthenticated) {
      throw new Error('User must be authenticated to update reservation notes');
    }

    try {
      const result = await reservationService.updateReservationNotes(reservationId, notes);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update reservation notes';
      console.error('Failed to update reservation notes:', err);
      throw new Error(errorMessage);
    }
  }, [token, isAuthenticated]);

  const applyProductToReservation = useCallback(async (reservationId: string, data: ApplyProductRequest): Promise<ApplyProductResponse> => {
    if (!token || !isAuthenticated) {
      throw new Error('User must be authenticated to apply product to reservation');
    }

    setIsLoading(true);

    try {
      const result = await reservationService.applyProduct(reservationId, data);

      // Find the affected booking to refresh its facility bookings
      const affectedBooking = bookings.find(b => b.id.startsWith(reservationId));

      // Refresh facility bookings if we found an affected booking
      if (affectedBooking) {
        await fetchFacilityBookings(affectedBooking.facilityId, affectedBooking.timeSlot.date);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to apply product to reservation';
      console.error('Failed to apply product to reservation:', err);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated, bookings, fetchFacilityBookings]);

  const rescheduleReservation = useCallback(async (reservationId: string, data: RescheduleReservationRequest): Promise<RescheduleReservationResponse> => {
    if (!token || !isAuthenticated) {
      throw new Error('User must be authenticated to reschedule reservation');
    }

    setIsLoading(true);

    try {
      const result = await reservationService.rescheduleReservation(reservationId, data);

      // Find the affected booking to refresh both old and new dates
      const affectedBooking = bookings.find(b => b.id.startsWith(reservationId));

      if (affectedBooking) {
        // Refresh old date
        await fetchFacilityBookings(affectedBooking.facilityId, affectedBooking.timeSlot.date);
        // Refresh new date
        await fetchFacilityBookings(data.newFacilityId || affectedBooking.facilityId, data.newDate);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reschedule reservation';
      console.error('Failed to reschedule reservation:', err);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated, bookings, fetchFacilityBookings]);

  const createGroupReservation = useCallback(async (data: GroupReservationRequest): Promise<GroupReservationResponse> => {
    if (!token || !isAuthenticated) {
      throw new Error('User must be authenticated to create group reservation');
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await reservationService.createGroupReservation(data);

      // Refresh bookings for all affected facilities and dates
      const uniqueFacilityDates = new Map<string, Set<string>>();
      data.facilityReservations.forEach(fr => {
        if (!uniqueFacilityDates.has(fr.facilityId)) {
          uniqueFacilityDates.set(fr.facilityId, new Set());
        }
        uniqueFacilityDates.get(fr.facilityId)!.add(fr.date);
      });

      // Fetch bookings in parallel for all affected facility/date combinations
      const fetchPromises: Promise<any>[] = [];
      uniqueFacilityDates.forEach((dates, facilityId) => {
        dates.forEach(date => {
          fetchPromises.push(fetchFacilityBookings(facilityId, date));
        });
      });
      await Promise.all(fetchPromises);

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create group reservation';
      console.error('Failed to create group reservation:', err);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated, fetchFacilityBookings]);

  const fetchAllFacilitiesBookings = useCallback(async (date: string): Promise<void> => {
    if (!token || !isAuthenticated) {
      console.warn('Cannot fetch bookings: not authenticated');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch bookings for all facilities in parallel
      const fetchPromises = facilities.map(facility =>
        fetchFacilityBookings(facility.id, date).catch(err => {
          console.error(`Failed to fetch bookings for facility ${facility.id}:`, err);
          return null;
        })
      );

      await Promise.all(fetchPromises);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch all facilities bookings';
      console.error('Failed to fetch all facilities bookings:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated, facilities, fetchFacilityBookings]);

  const fetchAvailableTrainers = useCallback(async (date: string, timeSlots: string[]): Promise<AvailableTrainer[]> => {
    if (!token || !isAuthenticated || !businessProfile?.id) {
      console.warn('Cannot fetch trainers: not authenticated or no business profile');
      return [];
    }

    try {
      const trainers = await trainerAssociationService.findAvailableTrainers(
        businessProfile.id,
        { date, timeSlots }
      );
      return trainers;
    } catch (err) {
      console.error('Failed to fetch available trainers:', err);
      return [];
    }
  }, [token, isAuthenticated, businessProfile?.id]);

  const value: BookingContextType = {
    bookings,
    facilities,
    selectedFacility,
    isLoading,
    error,
    businessProfile,
    facilityTimeSlots,
    setSelectedFacility,
    createBooking,
    updateBooking,
    deleteBooking,
    togglePaymentStatus,
    rescheduleBooking,
    isTimeSlotBooked,
    refreshBusinessProfile: fetchBusinessProfile,
    fetchFacilityBookings,
    getReservationSlots,
    cancelReservationSlots,
    toggleReservationPayment,
    updateReservationNotes,
    applyProductToReservation,
    rescheduleReservation,
    createGroupReservation,
    fetchAllFacilitiesBookings,
    fetchAvailableTrainers,
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};

// Helper function to parse time string to minutes
const parseTime = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

// Helper function to add one hour to a time string
const addOneHour = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const newHour = (hours + 1) % 24;
  return `${newHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};
